import { type ColumnDef, ErrorState, RelatedList, Spinner } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { formatCurrency, formatDate, humanize } from '../objects/format';
import { type ObjectRow, parseObjectListResponse, relatedUrl } from '../objects/objectsApi';
import { recordHref } from '../objects/routes';

const CONTRACT_TYPES: Record<string, string> = { nda: 'NDA', msa: 'MSA', work_order: 'Work Order', change_order: 'Change Order', other: 'Other' };

const contractColumns: ColumnDef<ObjectRow>[] = [
  { key: 'type', label: 'Type', render: (r) => CONTRACT_TYPES[String(r.type ?? '')] ?? humanize(r.type) },
  { key: 'name', label: 'Contract', primary: true },
  { key: 'status', label: 'Status', render: (r) => humanize(r.status) },
  { key: 'start_date', label: 'Start', render: (r) => formatDate(r.start_date) },
  { key: 'end_date', label: 'End', render: (r) => formatDate(r.end_date) },
];

const orderColumns: ColumnDef<ObjectRow>[] = [
  { key: 'name', label: 'Order', primary: true },
  { key: 'quote_name', label: 'Quote', link: { object: 'quotes', idKey: 'quote_id' } },
  { key: 'status', label: 'Status', render: (r) => humanize(r.status) },
  { key: 'effective_date', label: 'Effective Date', render: (r) => formatDate(r.effective_date) },
  { key: 'amount', label: 'Amount', align: 'right', figures: true, render: (r) => formatCurrency(r.amount) },
];

const assetColumns: ColumnDef<ObjectRow>[] = [
  { key: 'name', label: 'Asset', primary: true },
  { key: 'product_name', label: 'Product', link: { object: 'products', idKey: 'product_id' } },
  { key: 'billing', label: 'Billing', render: (r) => humanize(r.billing) },
  { key: 'start_date', label: 'Start', render: (r) => formatDate(r.start_date) },
  { key: 'end_date', label: 'End', render: (r) => formatDate(r.end_date) },
];

const LISTS = [
  { key: 'contracts', title: 'Contracts', object: 'contracts', columns: contractColumns, empty: 'No contracts yet' },
  // Orders have no record page yet: their rows are not links.
  { key: 'orders', title: 'Orders', object: undefined, columns: orderColumns, empty: 'No orders yet' },
  { key: 'assets', title: 'Assets', object: 'assets', columns: assetColumns, empty: 'No assets yet' },
] as const;

type Lists = Record<string, { rows: ObjectRow[]; total: number }>;

/**
 * The Contracting tab: the deal's paper (NDAs, MSAs, work and change orders),
 * the order Closed Won made from the primary quote, and the assets it put on the account.
 */
export function ContractingTab({ opportunityId, onNavigate }: { opportunityId: string; onNavigate: (href: string) => void }): JSX.Element {
  const [load, setLoad] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; lists: Lists }>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ state: 'loading' });
    Promise.all(
      LISTS.map(async (list) => {
        const url = relatedUrl('opportunities', opportunityId, list.key, { limit: 50, offset: 0 });
        const page = await parseObjectListResponse(await authenticatedFetch(url, { signal: controller.signal }));
        return [list.key, { rows: page.rows, total: page.total }] as const;
      }),
    )
      .then((entries) => setLoad({ state: 'ready', lists: Object.fromEntries(entries) }))
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setLoad({ state: 'error', message: e instanceof Error ? e.message : 'Could not load.' });
      });
    return () => controller.abort();
  }, [opportunityId, attempt]);

  if (load.state === 'loading') return <Spinner label="Loading contracts, orders and assets…" />;
  if (load.state === 'error') return <ErrorState title="Could not load contracting" description={load.message} onRetry={() => setAttempt((n) => n + 1)} />;

  return (
    <div className="flex flex-col gap-4">
      {LISTS.map((list) => {
        const { rows, total } = load.lists[list.key] ?? { rows: [], total: 0 };
        return (
          <RelatedList<ObjectRow>
            key={list.key}
            title={list.title}
            columns={[...list.columns]}
            rows={rows}
            total={total}
            previewCount={50}
            getRowId={(r) => r.id}
            {...(list.object ? { object: list.object } : {})}
            recordHref={recordHref}
            onNavigate={onNavigate}
            emptyLabel={list.empty}
            compactEmpty
          />
        );
      })}
    </div>
  );
}
